import { PrivacyPolicy } from '@teko/privacy-policy';
import { useCallback, useState, Dispatch, SetStateAction } from 'react';
import { Checkbox, ThemeProps, styled, Divider } from '@teko/ds-mobile';
import { t } from 'i18n';
import { InforOutlined } from '@teko/ds-icon';
import { useTenantConfig } from '../hooks';
import { PolicyTypeId } from 'types';
import { getTrackingData, trackManually } from '@teko/tracking';

export const PrivacyPolicies = (props: {
  policies: PrivacyPolicy[];
  trackingScreenName: string;
  setPolicies: Dispatch<SetStateAction<PrivacyPolicy[]>>;
  isLoggedIn: boolean;
  hideWarning?: boolean;
  disableChangeApproval?: boolean;
  originalPolicies?: PrivacyPolicy[];
}) => {
  const {
    policies,
    trackingScreenName,
    setPolicies,
    isLoggedIn,
    hideWarning,
    disableChangeApproval,
    originalPolicies,
  } = props;
  const [selectAllOption, setSelectAllOption] = useState<boolean>(() =>
    policies.every(
      (v) => v.policyTypeId === PolicyTypeId.TYPE_1 || v.isApproved,
    ),
  );
  const { isMobile } = useTenantConfig();

  const displayPolicies = [...policies];
  /**
   * Thêm 'Chọn tất cả' khi số lượng policies có PolicyTypeId khác TYPE_1 >= 2
   * */
  if (
    policies.filter((policy) => policy.policyTypeId !== PolicyTypeId.TYPE_1)
      .length >= 2
  ) {
    const policyType1Index = policies.findLastIndex?.(
      (policy) => policy.policyTypeId === PolicyTypeId.TYPE_1,
    );
    const selectAllOptionIndex =
      policyType1Index === -1 ? 0 : policyType1Index + 1;

    displayPolicies.splice(selectAllOptionIndex, 0, {
      id: PolicyTypeId.TYPE_ALL,
      policyTypeId: PolicyTypeId.TYPE_ALL,
      isApprovalRequired: false,
      isApproved: selectAllOption,
      description: t('Chọn tất cả'),
      language: '',
      version: 0,
    });
  }

  const changePolicyApproval = useCallback(
    (id: number, state: boolean) => {
      const newPolicies = policies.map((policy) => {
        if (policy.id === id) {
          trackManually(
            state ? 'checkPrivacy' : 'uncheckPrivacy',
            'privacyContent',
            '',
            JSON.stringify({
              screenName: trackingScreenName,
              policy: policy.description,
            }),
          );
          return { ...policy, isApproved: state };
        } else return policy;
      });
      setPolicies(newPolicies);

      const isSelectedAllPolicies = newPolicies.every((v) =>
        v.policyTypeId === PolicyTypeId.TYPE_1 ? true : v.isApproved,
      );
      setSelectAllOption(isSelectedAllPolicies);
    },
    [policies],
  );

  return (
    <div
      style={{ textAlign: 'left' }}
      {...getTrackingData({
        isTrackContent: true,
        contentName: 'showPrivacPolicy',
        regionName: 'privacyContent',
        payload: JSON.stringify({
          screenName: trackingScreenName,
          policies,
        }),
      })}
    >
      {displayPolicies.map((policy) => {
        // find the outermost html tag of in the string
        const HTMLTag =
          policy.description[0] === '<'
            ? policy.description
                // start at 1 since we don't want to include '<'
                .substring(1, policy.description.indexOf('>'))
                // split by space in case html tag has attributes in it
                .split(' ')
            : '';

        switch (policy.policyTypeId) {
          case PolicyTypeId.TYPE_1:
            return !hideWarning ? (
              <WarningPolicyWrapper isMobile={isMobile} key={policy.id}>
                <InforOutlined
                  size={24}
                  color="pending600"
                  style={{
                    flex: '0 0 auto',
                  }}
                />
                {isLoggedIn ? (
                  <div>
                    {t(
                      'Chúng tôi có các cập nhật liên quan đến việc xử lý dữ liệu cá nhân. Vui lòng kiểm tra và xác nhận. Các chính sách có đánh dấu * là bắt buộc xác nhận để tiếp tục sử dụng dịch vụ.',
                    )}
                  </div>
                ) : (
                  <HTMLContentContainer
                    dangerouslySetInnerHTML={{ __html: policy.description }}
                  />
                )}
              </WarningPolicyWrapper>
            ) : null;
          case PolicyTypeId.TYPE_ALL:
            return (
              <div key={policy.id}>
                {hideWarning ? null : <Divider color="border" />}
                <SelectablePolicyWrapper>
                  <Checkbox
                    height="min-content"
                    checked={selectAllOption}
                    onChange={(state) => {
                      trackManually(
                        state ? 'checkAllPrivacy' : 'uncheckAllPrivacy',
                        'privacyContent',
                        '',
                        JSON.stringify({
                          screenName: trackingScreenName,
                          policy: policy.description,
                        }),
                      );
                      setSelectAllOption(state);
                      setPolicies((prev) => {
                        return prev.map((policyValue) => ({
                          ...policyValue,
                          isApproved:
                            policyValue.policyTypeId !== PolicyTypeId.TYPE_1
                              ? disableChangeApproval &&
                                policyValue.isApprovalRequired
                                ? true
                                : state
                              : policyValue.isApproved,
                        }));
                      });
                    }}
                    border={disableChangeApproval ? '' : '1px solid black'}
                    style={{ borderRadius: '2px' }}
                  />
                  <div>{policy.description}</div>
                </SelectablePolicyWrapper>
              </div>
            );
          default:
            return (
              <SelectablePolicyWrapper key={policy.id}>
                <Checkbox
                  height="min-content"
                  checked={policy.isApproved}
                  onChange={(state) =>
                    disableChangeApproval &&
                    policy.isApprovalRequired &&
                    originalPolicies?.find((p) => p.id === policy.id)
                      ?.isApproved
                      ? null
                      : changePolicyApproval(policy.id, state)
                  }
                  disabled={
                    disableChangeApproval &&
                    policy.isApprovalRequired &&
                    originalPolicies?.find((p) => p.id === policy.id)
                      ?.isApproved
                  }
                  border={disableChangeApproval ? '' : '1px solid black'}
                  style={{ borderRadius: '2px' }}
                />
                <HTMLContentContainer
                  isRequire={policy.isApprovalRequired}
                  HTMLTag={HTMLTag[0]} // first element in HTML tag will be the tag name
                  dangerouslySetInnerHTML={{
                    __html: !!HTMLTag
                      ? policy.description
                      : `<div>${policy.description}</div>`,
                  }}
                />
              </SelectablePolicyWrapper>
            );
        }
      })}
    </div>
  );
};

const WarningPolicyWrapper = styled.div(
  ({ theme, isMobile }: ThemeProps & { isMobile?: boolean }) => `
    padding: 8px 12px;
    background-color: ${theme?.colors.pending100};
    color: ${theme?.colors.pending600};
    display: flex;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: ${isMobile ? '8px' : '16px'};
`,
);

const SelectablePolicyWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 0.5rem;
`;

const HTMLContentContainer = styled.div(
  ({
    theme,
    isRequire,
    HTMLTag,
  }: ThemeProps & { isRequire?: boolean; HTMLTag?: string }) => `
  display: inline-block;
  & > p {
    margin: 0px;
  }
  ${
    isRequire
      ? !!HTMLTag
        ? `& > ${HTMLTag}:first-child::before {
        content: "*";
        margin-right: 0.5rem;
        color: ${theme?.colors.error500};
      }`
        : `& > div:first-child::before {
        content: "*";
        margin-right: 0.5rem;
        color: ${theme?.colors.error500};
      }`
      : ''
  }
`,
);
